import type { AnnualTimetableCell } from '../types/domain';
import { loadAnnualTimetableGradeCellsLocalCache } from '../services/annualTimetableService';
import { loadAnnualProgressDataCache } from './annualProgressDataCache';
import { loadProgramProgressDataCache } from './programProgressDataCache';
import { buildAnnualCellMap, gradeIdFromAnnualCellKey } from './annualTimetable';

/** 연간진도·대시보드: aggregate·ppd·학년별 LS·(선택)네트워크 셀을 한 map으로 합친다. */
export function resolveMergedTimetableCellsByKey(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeIds: string[];
  networkCellsByKey?: Record<string, AnnualTimetableCell>;
}): Record<string, AnnualTimetableCell> {
  const { workspaceId, academicYear, termNumber, gradeIds, networkCellsByKey } = params;
  const uniqueGradeIds = Array.from(new Set(gradeIds.map((id) => id.trim()).filter(Boolean)));
  if (!workspaceId.trim() || uniqueGradeIds.length === 0) return {};

  const ap = loadAnnualProgressDataCache(workspaceId, academicYear, termNumber);
  const ppdCellsByKey: Record<string, AnnualTimetableCell> = {};
  uniqueGradeIds.forEach((gradeId) => {
    const ppd = loadProgramProgressDataCache(workspaceId, academicYear, termNumber, gradeId);
    Object.assign(ppdCellsByKey, ppd.state.cellsByKey);
  });

  let merged = mergeTimetableCellsByKeyFromLocalSources({
    workspaceId,
    academicYear,
    termNumber,
    gradeIds: uniqueGradeIds,
    apCellsByKey: ap.found ? ap.state.cellsByKey : {},
    ppdCellsByKey,
  });

  if (networkCellsByKey && Object.keys(networkCellsByKey).length > 0) {
    const networkFiltered = filterCellsByKeyForGrades(networkCellsByKey, uniqueGradeIds);
    for (const [key, netCell] of Object.entries(networkFiltered)) {
      const localCell = merged[key];
      if (!localCell) {
        merged[key] = netCell;
        continue;
      }
      const localTs = Date.parse(String(localCell.updated_at ?? '')) || 0;
      const netTs = Date.parse(String(netCell.updated_at ?? '')) || 0;
      /** 저장 직후 LS가 먼저 갱신된 경우 옛 RQ가 덮지 않게, 그 외에는 최신 쪽을 사용 */
      if (netTs >= localTs) merged[key] = netCell;
    }
  }

  return merged;
}

export function mergedTimetableCellsReadyForGrades(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeIds: string[]
): boolean {
  return Object.keys(resolveMergedTimetableCellsByKey({ workspaceId, academicYear, termNumber, gradeIds })).length > 0;
}

/**
 * aggregate(`annualProgressData`·`programProgressData`) embedded `cellsByKey`를 우선 merge하고,
 * 학년별 `cp.annualTimetableCells.v2.*` LS로 aggregate에 없는 셀만 gap-fill한다.
 * (일부 주만 aggregate에 있어도 다른 날짜 `<` `>` 이동 시 LS로 나머지 주를 채운다.)
 */
export function mergeTimetableCellsByKeyFromLocalSources(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeIds: string[];
  apCellsByKey: Record<string, AnnualTimetableCell>;
  ppdCellsByKey: Record<string, AnnualTimetableCell>;
}): Record<string, AnnualTimetableCell> {
  const { workspaceId, academicYear, termNumber, gradeIds, apCellsByKey, ppdCellsByKey } = params;
  const merged: Record<string, AnnualTimetableCell> = {
    ...ppdCellsByKey,
    ...apCellsByKey,
  };
  if (!workspaceId?.trim()) return merged;

  const uniqueGradeIds = Array.from(new Set(gradeIds.map((id) => id.trim()).filter(Boolean)));
  for (const gradeId of uniqueGradeIds) {
    const local = loadAnnualTimetableGradeCellsLocalCache(workspaceId, academicYear, termNumber, gradeId);
    if (!local.found || local.rows.length === 0) continue;
    const localMap = buildAnnualCellMap(local.rows);
    for (const [key, cell] of Object.entries(localMap)) {
      // gap-fill + 연간시간표 LS 우선(저장 직후 aggregate·ppd에 옛 셀이 남아 일부 편제만 보이는 문제 방지)
      merged[key] = cell;
    }
  }
  return merged;
}

export function aggregateEmbeddedTimetableCellsReady(
  cellsByKey: Record<string, AnnualTimetableCell>
): boolean {
  return Object.keys(cellsByKey).length > 0;
}

export function localTimetableCellsReadyForGrades(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeIds: string[]
): boolean {
  if (!workspaceId?.trim()) return false;
  return gradeIds.some((gradeId) => {
    const id = gradeId.trim();
    if (!id) return false;
    const local = loadAnnualTimetableGradeCellsLocalCache(workspaceId, academicYear, termNumber, id);
    return local.found && local.rows.length > 0;
  });
}

export function collectLocalTimetableCellsForGrades(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeIds: string[]
): AnnualTimetableCell[] {
  if (!workspaceId?.trim()) return [];
  const out: AnnualTimetableCell[] = [];
  const seen = new Set<string>();
  for (const gradeId of gradeIds) {
    const id = gradeId.trim();
    if (!id) continue;
    const local = loadAnnualTimetableGradeCellsLocalCache(workspaceId, academicYear, termNumber, id);
    if (!local.found) continue;
    for (const row of local.rows) {
      const key = `${row.grade_id}|${row.monday_iso}-${row.weekday_key}-${row.slot_index}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(row);
    }
  }
  return out;
}

/** aggregate `cellsByKey`에서 요청 학년만 남김 */
export function filterCellsByKeyForGrades(
  cellsByKey: Record<string, AnnualTimetableCell>,
  gradeIds: string[]
): Record<string, AnnualTimetableCell> {
  const allowed = new Set(gradeIds.map((id) => id.trim()).filter(Boolean));
  if (allowed.size === 0) return {};
  const out: Record<string, AnnualTimetableCell> = {};
  for (const [key, cell] of Object.entries(cellsByKey)) {
    const gid = (gradeIdFromAnnualCellKey(key) ?? cell.grade_id ?? '').trim();
    if (gid && allowed.has(gid)) out[key] = cell;
  }
  return out;
}

/** aggregate `cellsByKey`에 한 학년 분량을 덮어쓰기 병합(탭 전환 시 학년별 누적) */
export function mergeTimetableCellsByKeyForGrade(
  existing: Record<string, AnnualTimetableCell>,
  gradeId: string,
  gradeCells: Record<string, AnnualTimetableCell>
): Record<string, AnnualTimetableCell> {
  const gid = gradeId.trim();
  if (!gid) return existing;
  const merged: Record<string, AnnualTimetableCell> = {};
  for (const [key, cell] of Object.entries(existing)) {
    const keyGrade = (gradeIdFromAnnualCellKey(key) ?? cell.grade_id ?? '').trim();
    if (keyGrade !== gid) merged[key] = cell;
  }
  return { ...merged, ...gradeCells };
}

/** 대시보드 `<` `>` 기준: 보는 주(`mondayIso`)에 해당 학년 연간셀이 aggregate·LS 어디에도 없으면 false */
export function dashboardViewWeekTimetableCellsReady(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  mondayIso: string;
}): boolean {
  const { workspaceId, academicYear, termNumber, gradeId, mondayIso } = params;
  const mon = (mondayIso ?? '').trim();
  const gid = (gradeId ?? '').trim();
  if (!workspaceId.trim() || !mon || !gid) return false;

  const weekPrefix = `${gid}|${mon}-`;

  const ap = loadAnnualProgressDataCache(workspaceId, academicYear, termNumber);
  if (ap.found) {
    for (const key of Object.keys(ap.state.cellsByKey)) {
      if (key.startsWith(weekPrefix)) return true;
    }
  }

  const ppd = loadProgramProgressDataCache(workspaceId, academicYear, termNumber, gid);
  if (ppd.found) {
    for (const key of Object.keys(ppd.state.cellsByKey)) {
      if (key.startsWith(weekPrefix)) return true;
    }
  }

  const local = loadAnnualTimetableGradeCellsLocalCache(workspaceId, academicYear, termNumber, gid);
  if (local.found && local.rows.some((row) => (row.monday_iso ?? '').trim() === mon)) {
    return true;
  }

  return false;
}
